import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ClipboardList, Save, AlertOctagon, Calendar, Send, Mic, Square, Sparkles } from 'lucide-react';
import useAppStore from '../store/appStore';
import {
    getLiveAudioService,
    type LiveAudioState,
} from '../services/liveAudioService';

interface CoachNotesPageProps {
    clientData: any;
    showNotification?: (msg: string, type: 'success' | 'error') => void;
}

const CoachNotesPage: React.FC<CoachNotesPageProps> = ({ clientData, showNotification }) => {
    const [notes, setNotes] = useState('');
    const [draftText, setDraftText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isListening, setIsListening] = useState(false);
    const [liveState, setLiveState] = useState<LiveAudioState>('idle');
    const [isProofreading, setIsProofreading] = useState(false);

    const quillRef = useRef<ReactQuill>(null);
    /** Text vor dem aktuellen Live-Turn (für Interim-Anzeige) */
    const draftBaseRef = useRef('');
    const liveAudio = useMemo(() => getLiveAudioService(), []);

    const dictatedCoachNote = useAppStore((state: any) => state.dictatedCoachNote);
    const setDictatedCoachNote = useAppStore((state: any) => state.setDictatedCoachNote);

    const isGodMode = !!clientData?._isGodMode || !!sessionStorage.getItem('GOD_MODE_ID');
    const clientId = clientData?.id || sessionStorage.getItem('GOD_MODE_ID');

    // Betty-Chat auf Coach-Akte: Diktat landet in der Haupt-Akte (bestehender Ghostwriter)
    useEffect(() => {
        if (dictatedCoachNote && dictatedCoachNote.trim() !== '') {
            if (quillRef.current) {
                const quill = quillRef.current.getEditor();
                const length = quill.getLength();
                const insertPos = length > 1 ? length - 1 : 0;
                const prefix = insertPos > 0 ? '\n\n' : '';

                quill.insertText(insertPos, prefix + dictatedCoachNote);
                quill.setSelection(quill.getLength(), 0);

                setNotes(quill.root.innerHTML);
            }
            setDictatedCoachNote('');
        }
    }, [dictatedCoachNote, setDictatedCoachNote]);

    const stopListeningFully = useCallback(() => {
        if (liveAudio.isDictationMode() || isListening) {
            liveAudio.stopSession();
        }
        setIsListening(false);
        draftBaseRef.current = draftText.trim();
    }, [liveAudio, isListening, draftText]);

    // Betty Live: State + User-Transkript → Vorbereitungsfeld
    useEffect(() => {
        const offState = liveAudio.onStateChange((next, detail) => {
            setLiveState(next);
            if (!liveAudio.isDictationMode()) {
                setIsListening(false);
                return;
            }
            if (next === 'connecting' || next === 'connected' || next === 'speaking_user' || next === 'speaking_lucy') {
                setIsListening(true);
            } else if (next === 'idle') {
                setIsListening(false);
            } else if (next === 'error') {
                setIsListening(false);
                showNotification?.(detail || 'Betty Live Diktat fehlgeschlagen.', 'error');
            }
        });

        const offTranscript = liveAudio.onUserTranscript(({ text, final }) => {
            if (!liveAudio.isDictationMode()) return;
            const base = draftBaseRef.current;
            const joined = [base, text].filter(Boolean).join(base && !base.endsWith(' ') ? ' ' : '');
            setDraftText(joined);
            if (final) {
                draftBaseRef.current = joined.trim();
            }
        });

        return () => {
            offState();
            offTranscript();
            if (liveAudio.isDictationMode()) {
                liveAudio.stopSession();
            }
        };
    }, [liveAudio, showNotification]);

    const toggleListening = async () => {
        if (isListening || liveAudio.isDictationMode()) {
            stopListeningFully();
            return;
        }

        draftBaseRef.current = draftText.trim();
        setIsListening(true);
        try {
            await liveAudio.startSession({
                mode: 'dictation',
                force: true,
                currentScreenContext: 'Coach-Akte Diktat',
            });
            showNotification?.('Betty Live hört zu — sprich jetzt. STOPP beendet das Diktat.', 'success');
        } catch (error: any) {
            setIsListening(false);
            showNotification?.(error?.message || 'Betty Live konnte nicht starten.', 'error');
        }
    };

    const handleAppendToNotes = () => {
        const textToAppend = draftText.trim();
        if (!textToAppend) return;

        stopListeningFully();

        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            const length = quill.getLength();
            const insertPos = length > 1 ? length - 1 : 0;
            const prefix = insertPos > 0 ? '\n\n' : '';

            quill.insertText(insertPos, prefix + textToAppend);
            quill.setSelection(quill.getLength(), 0);

            setNotes(quill.root.innerHTML);
        }

        setDraftText('');
        draftBaseRef.current = '';
        showNotification?.('Text in die Akte eingefügt — nicht vergessen zu sichern.', 'success');
    };

    const handleProofreadWithBetty = async () => {
        const raw = draftText.trim();
        if (!raw || isProofreading) return;

        stopListeningFully();
        setIsProofreading(true);
        try {
            const functions = getFunctions(getApp(), 'europe-west1');
            const askBetty = httpsCallable(functions, 'askBettyAppAi');
            const salon =
                clientData?.firmenname || clientData?.vorname || 'dieser Mandant';
            const response = await askBetty({
                message: raw,
                route: 'coach-akte-lektor',
                systemDirectives: [
                    'Du bist Lektorin für interne Coach-Akten-Notizen bei PreisExpert (Friseur-Coaching).',
                    `Kontext: Notiz betrifft den Mandanten „${salon}".`,
                    'Korrigiere Orthografie, Grammatik und Zeichensetzung. Formuliere klar, knapp und professionell.',
                    'Behalte Inhalt, Fakten und Absicht 1:1. Erfinde nichts. Keine Anrede, keine Meta-Kommentare, keine Aufzählung deiner Änderungen.',
                    'Antworte AUSSCHLIESSLICH mit dem fertigen Notiztext — ohne Anführungszeichen, ohne Einleitung.',
                ],
                chatHistory: [],
            });
            const data = response.data as { success?: boolean; text?: string };
            const polished = String(data?.text || '').trim();
            if (!polished) {
                showNotification?.('Betty hat keinen Text zurückgegeben.', 'error');
                return;
            }
            setDraftText(polished);
            draftBaseRef.current = polished;
            showNotification?.('Betty hat den Text lektoriert — prüfe und füge ihn in die Akte ein.', 'success');
        } catch (error: any) {
            console.error('Coach-Akte Lektorat fehlgeschlagen:', error);
            showNotification?.(
                `Lektorat fehlgeschlagen: ${error?.message || 'Unbekannter Fehler'}`,
                'error'
            );
        } finally {
            setIsProofreading(false);
        }
    };

    const insertTimestamp = useCallback(
        (e?: React.MouseEvent) => {
            if (e) e.preventDefault();
            if (!quillRef.current) return;

            const quill = quillRef.current.getEditor();
            const length = quill.getLength();
            const insertPos = length > 1 ? length - 1 : 0;
            const prefix = insertPos > 0 ? '\n\n' : '';
            const dateStr = `--- Eintrag vom ${new Date().toLocaleDateString('de-DE')} ---`;

            quill.insertText(insertPos, prefix + dateStr);
            quill.formatText(insertPos + prefix.length, dateStr.length, 'bold', true);
            quill.formatText(insertPos + prefix.length, dateStr.length, 'color', '#E6C299');
            quill.insertText(insertPos + prefix.length + dateStr.length, '\n');
            quill.setSelection(quill.getLength(), 0);

            setNotes(quill.root.innerHTML);
        },
        []
    );

    const fetchNotes = useCallback(async () => {
        if (!clientId || !isGodMode) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const docRef = doc(db, 'coach_notes', clientId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) setNotes(docSnap.data().text || '');
        } catch (error) {
            console.error('Fehler', error);
        } finally {
            setIsLoading(false);
        }
    }, [clientId, isGodMode]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    const handleSave = async (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        if (!clientId || !isGodMode) return;

        setIsSaving(true);
        try {
            const docRef = doc(db, 'coach_notes', clientId);
            await setDoc(docRef, { text: notes, updatedAt: new Date().toISOString() }, { merge: true });
            if (showNotification) showNotification('Coach-Akte gesichert!', 'success');
        } catch (error) {
            if (showNotification) showNotification('Fehler beim Speichern.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const quillModules = useMemo(
        () => ({
            toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ color: [] }, { background: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['clean'],
            ],
        }),
        []
    );

    if (!isGodMode) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh] bg-[#0a0b0e] border border-red-900/50 rounded-2xl p-8 text-center animate-fade-in">
                <AlertOctagon size={64} className="text-red-500 mb-6" />
                <h1 className="text-2xl font-black text-red-500 uppercase tracking-widest mb-2">
                    ZUGRIFF VERWEIGERT
                </h1>
                <p className="text-slate-400">
                    Diese Sektion unterliegt der höchsten Geheimhaltungsstufe.
                    <br />
                    Nur autorisiertes Coaching-Personal im God-Mode hat hier Zutritt.
                </p>
            </div>
        );
    }

    return (
        <div className="animate-fade-in pb-8">
            <style
                dangerouslySetInnerHTML={{
                    __html: `
                .coach-notes-quill .ql-toolbar.ql-snow { border-color: #1e293b; background: #0f1015; border-radius: 12px 12px 0 0; border-bottom: none; }
                .coach-notes-quill .ql-container.ql-snow { border-color: #1e293b; color: #f8fafc; font-size: 15px; font-family: inherit; border-radius: 0 0 12px 12px; background: #0a0b0e; }
                .coach-notes-quill .ql-editor { min-height: 250px; padding: 24px; line-height: 1.7; }
                .coach-notes-quill .ql-snow .ql-stroke { stroke: #cbd5e1; }
                .coach-notes-quill .ql-snow .ql-fill { fill: #cbd5e1; }
                .coach-notes-quill .ql-snow .ql-picker { color: #cbd5e1; }
                .coach-notes-quill .ql-editor.ql-blank::before { color: #475569; font-style: normal; }
                .coach-notes-quill .ql-editor strong { color: #E6C299; }
                .coach-notes-quill .ql-editor h1, .coach-notes-quill .ql-editor h2, .coach-notes-quill .ql-editor h3 { color: #E6C299; margin-top: 1em; margin-bottom: 0.5em; }
            `,
                }}
            />

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black text-white uppercase tracking-widest flex items-center gap-3">
                        <div className="p-3 bg-red-600/10 border border-red-600/30 rounded-xl">
                            <ClipboardList size={28} className="text-red-500" />
                        </div>
                        Coach-Akte
                    </h1>
                    <p className="text-sm text-slate-400 mt-2 ml-16 max-w-xl">
                        Interne Übergabe-Notizen und strategische Informationen für{' '}
                        <strong className="text-[#E6C299]">
                            {clientData?.firmenname || clientData?.vorname || 'diesen Mandanten'}
                        </strong>
                        .
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSave}
                        disabled={isSaving || isLoading}
                        className="px-6 py-3 bg-red-600 hover:bg-red-500 border border-red-500 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-[0_0_20px_rgba(220,38,38,0.3)] transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                        {isSaving ? (
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <Save size={16} />
                        )}
                        Akte sichern
                    </button>
                </div>
            </div>

            <div className="bg-[#15171e] border border-red-900/30 rounded-2xl p-4 shadow-2xl relative mb-6">
                {isLoading ? (
                    <div className="h-[300px] flex flex-col items-center justify-center gap-4 text-red-500/50">
                        <span className="w-8 h-8 border-4 border-red-500/20 border-t-red-500 rounded-full animate-spin" />
                        <p className="text-xs font-bold tracking-widest uppercase">Entschlüssele Akte...</p>
                    </div>
                ) : (
                    <div className="coach-notes-quill">
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={notes}
                            onChange={setNotes}
                            modules={quillModules}
                            placeholder="Historie der Coach-Akte..."
                        />
                    </div>
                )}
            </div>

            <div className="bg-[#0f1015] border border-slate-800 rounded-2xl p-5 shadow-xl relative mt-8">
                <div className="absolute -top-3 left-6 bg-[#0a0b0e] px-2 text-[10px] font-black uppercase tracking-widest text-[#E6C299]">
                    Manuelle Eingabe & Vorbereitung
                </div>

                <div className="flex flex-col gap-4">
                    {isListening && (
                        <div className="text-red-400 text-[10px] uppercase font-black tracking-widest bg-red-500/10 px-3 py-1.5 rounded-full border border-red-500/20 inline-flex items-center gap-2 animate-pulse self-start">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            {liveState === 'connecting'
                                ? 'Betty Live verbindet…'
                                : 'Betty Live hört zu — STOPP beendet'}
                        </div>
                    )}

                    <textarea
                        id="native-dictation-box"
                        value={draftText}
                        onChange={(e) => {
                            setDraftText(e.target.value);
                            if (!isListening) draftBaseRef.current = e.target.value;
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAppendToNotes();
                            }
                        }}
                        placeholder="Mit Betty Live diktieren oder tippen — danach optional lektorieren und in die Akte einfügen…"
                        className={`w-full bg-[#15171e] border text-white p-4 rounded-xl resize-y min-h-[100px] focus:outline-none placeholder-slate-600 text-sm leading-relaxed overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent ${
                            isListening
                                ? 'border-red-500/40'
                                : 'border-slate-800 focus:border-[#E6C299]/50'
                        }`}
                    />

                    <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => void toggleListening()}
                                disabled={isLoading || isProofreading}
                                className={`px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all justify-center border ${
                                    isListening
                                        ? 'bg-red-500/20 border-red-500 text-red-400 shadow-[0_0_15px_rgba(239,68,68,0.2)]'
                                        : 'bg-[#15171e] border-slate-700 text-slate-300 hover:text-[#E6C299] hover:border-[#E6C299]/50'
                                }`}
                                title={isListening ? 'Betty Live Diktat stoppen' : 'Mit Betty Live diktieren'}
                            >
                                {isListening ? (
                                    <>
                                        <Square size={14} />
                                        Stopp
                                    </>
                                ) : (
                                    <>
                                        <Mic size={16} />
                                        Betty Live diktieren
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => void handleProofreadWithBetty()}
                                disabled={isLoading || isProofreading || !draftText.trim() || isListening}
                                className="px-4 py-3 bg-[#15171e] border border-[#E6C299]/40 text-[#E6C299] hover:bg-[#E6C299]/10 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Betty korrigiert Orthografie und Formulierung"
                            >
                                {isProofreading ? (
                                    <span className="w-4 h-4 border-2 border-[#E6C299]/30 border-t-[#E6C299] rounded-full animate-spin" />
                                ) : (
                                    <Sparkles size={16} />
                                )}
                                Betty lektoriert
                            </button>

                            <button
                                type="button"
                                onClick={insertTimestamp}
                                disabled={isLoading}
                                className="px-4 py-3 bg-[#15171e] border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all justify-center"
                            >
                                <Calendar size={16} />
                                Datum einfügen
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={handleAppendToNotes}
                            disabled={!draftText.trim() || isProofreading}
                            className="w-full lg:w-auto px-6 py-3 bg-[#E6C299]/10 hover:bg-[#E6C299]/20 border border-[#E6C299]/30 text-[#E6C299] rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Send size={16} />
                            In Akte einfügen
                        </button>
                    </div>
                </div>
            </div>

            <div className="mt-6 p-4 bg-red-900/10 border border-red-900/30 rounded-xl flex items-start gap-4">
                <AlertOctagon size={20} className="text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-400 leading-relaxed">
                    <strong className="text-red-400">Hinweis:</strong> Betty Live diktieren →
                    optional Betty lektorieren → „In Akte einfügen“ → oben „Akte sichern“.
                </p>
            </div>
        </div>
    );
};

export default CoachNotesPage;
